import { Suspense, useEffect, useState } from "react";
import { Navigate, Route, Routes, useLocation, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { PreLoginRoutes } from "./routes/preLoginRoutes";
import { PostLoginRoutes } from "./routes/postLoginRoutes";
import ProtectedRoutes from "./utils/validations/protectedRoutes";
import Loader from "./components/Loader";
import useApi from "./hooks/useApi";
import loginService from "./api/services/loginService";
import { clearUser, setUser } from "./redux/slices/userSlice";
import {
  broadcastSSOResultAndClose,
  consumeSSOPopupFlag,
} from "./utils/ssoPopup";

function AppRoutes() {
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();

  const [authChecked, setAuthChecked] = useState(false);

  const { callApi: userLoginData } = useApi(
    loginService.userLoginData,
    null,
    false,
  );

  useEffect(() => {
    const ssoError = new URLSearchParams(location.search).get("error");

    const isPreLoginRoute = PreLoginRoutes.some(
      (route) => route.path === location.pathname,
    );

    // If this tab is the SSO popup we opened from the parent, hand the
    // result back via BroadcastChannel and close. The parent always sits
    // on a PreLogin route (typically /login) for the duration of SSO; the
    // popup lands on a post-login route after the backend redirect. That
    // path-based check is the reliable discriminator — sessionStorage is
    // inherited from opener to popup at window.open() time, so any flag
    // the parent sets there leaks into the popup. Leave authChecked=false
    // so <Loader /> stays visible until window.close() fires; rendering
    // the post-login route here would leak it into the popup if close is
    // delayed by the browser.
    if (!isPreLoginRoute && consumeSSOPopupFlag()) {
      broadcastSSOResultAndClose(ssoError);
      return;
    }

    if (ssoError && !isPreLoginRoute) {
      navigate(`/login?error=${encodeURIComponent(ssoError)}`, {
        replace: true,
      });
      return;
    }

    if (isPreLoginRoute) {
      setAuthChecked(true);
      return;
    }

    let mounted = true;

    userLoginData({}, (response, err) => {
      if (!mounted) return;

      if (response?.code === 200) {
        dispatch(setUser(response.data.result));
      } else {
        dispatch(clearUser());
      }

      setAuthChecked(true);
    });

    return () => {
      mounted = false;
    };
    // eslint-disable-next-line
  }, [location.pathname, location.search]);

  if (!authChecked) return <Loader />;

  return (
    <Suspense fallback={<Loader />}>
      <Routes>
        <Route path="/" element={<Navigate to="/login" />} />

        {PreLoginRoutes.map((route) => (
          <Route key={route.path} path={route.path} element={route.element} />
        ))}

        {PostLoginRoutes.map((route) => (
          <Route
            key={route.id}
            path={route.path}
            element={
              <ProtectedRoutes>
                <route.element />
              </ProtectedRoutes>
            }
          />
        ))}
      </Routes>
    </Suspense>
  );
}

export default AppRoutes;
